import {observer} from 'mobx-react-lite'
import React from 'react'
import tw from 'tailwind-styled-components'
import {Lang} from 'yougile-lang'
import {ArrUtil} from 'yougile-util'
import {DataObjectId, Hub, isDeletedEntity} from 'yougile-model'
import {Link} from '@components/common/button/button'
import {chatStore} from '@components/subtasks-old/chat-store'
import {ShowParentTasks} from '@components/task-card/show-parent-tasks'
import {useForceUpdate, useMemoOne, useSyncEvent} from '@containers/hooks'
import {cn} from '@containers/html-utils'
import {ChatUtils} from '@extensions/chat-utils'
import {getAllMirroredHubs} from '@extensions/mirrored-tasks/get-all-mirrored-hubs'
import {MirroredTasks} from '@extensions/mirrored-tasks/mirrored-tasks'
import {SubtaskUtil} from '@extensions/subtask-util'
import {Task as TaskUtil} from '@extensions/task'
import {ParentTasksStore, StoreContext, useStore} from './parent-tasks-store'
import TaskIcon from './task-icon.svg'

const TwTaskCnt = tw.div`
    relative
    cursor-pointer
    leading-main
    text-main-old
    text-12
    py-8
    pl-4
    w-full
    hover:bg-main-old
`

const Task = (props: {
  id: DataObjectId
  title: string
  isParent: boolean
  isCompleted: boolean
  className?: string
}) => {
  const [state, store] = useStore()
  return (
    <TwTaskCnt
      className={cn(
        !store.onBoard &&
          'with-border border mb-8 p-4 hover:bg-project-hover-old rounded-4',
        'flex',
        props.className
      )}
      onClick={e => {
        e.stopPropagation()
        ChatUtils.openChatById(props.id)
      }}
    >
      <TaskIcon
        className={cn(
          'ml-4 shrink-0 align-middle',
          props.isCompleted ? 'text-success-old' : 'text-obscure-constant-old'
        )}
      />
      {props.isParent && (
        <ShowParentTasks
          opened={state.opened.has(props.id)}
          onToggle={() => store.toggleOpen(props.id)}
          className="mt-4 mr-4"
        />
      )}
      <span
        className={cn(
          'leading-6 break-words min-w-0',
          store.taskId === props.id && 'font-semibold',
          props.isCompleted && 'text-third-old'
        )}
      >
        {props.title}
      </span>
    </TwTaskCnt>
  )
}

const TaskSelected = tw.div`
    absolute
    bg-accent-old
    w-4
    top-0
    bottom-0
    left-0
    h-full
`

const ParentTask = observer(
  (props: {taskId: DataObjectId; last?: boolean; subtasks?: Hub[]}) => {
    const [state, store] = useStore()
    const forceUpdate = useForceUpdate()

    const task = TaskUtil.getById(props.taskId)
    useSyncEvent('current', {filter: props.taskId}, forceUpdate)

    if (!task || task?.deleted) {
      return null
    }

    const subtasks =
      props.subtasks ??
      ArrUtil.filterUndefined(
        SubtaskUtil.getSubtaskIds(task)
          .map(x => TaskUtil.getById(x))
          .filter(t => t && !isDeletedEntity(t))
      )
    const selected = store.onBoard && chatStore.isOpened(props.taskId)

    return (
      <div className="select-none">
        <div className={cn('relative')}>
          <Task
            id={props.taskId}
            title={task?.title || ''}
            isParent={subtasks?.length ? subtasks?.length > 0 : false}
            isCompleted={task?.data?.gtd?.state?.stage === 'done'}
          />
          {selected && store.taskId !== props.taskId && (
            <TaskSelected className="-left-1" />
          )}
        </div>
        {task?.id && state.opened.has(task.id) && (
          <div
            className={cn(
              store.onBoard && 'with-border border-0 border-l',
              'ml-20'
            )}
          >
            {subtasks?.map((x, i) => (
              <div key={i} className="relative">
                {x?.id && <ParentTask taskId={x?.id} />}
              </div>
            ))}
          </div>
        )}
      </div>
    )
  }
)
export const ParentTasks = observer(
  (props: {taskId: DataObjectId; onBoard?: boolean; onClose: () => void}) => {
    const {taskId, onBoard} = props

    const store = useMemoOne(
      () => new ParentTasksStore(taskId, !!onBoard),
      [taskId, onBoard]
    )

    const parentTaskIds = store.getParentTaskIds(taskId)

    if (!parentTaskIds.length) {
      return null
    }

    let subtasks: Hub[] | undefined = undefined

    // the same
    if (parentTaskIds.length === 1 && parentTaskIds[0] === taskId) {
      const task = TaskUtil.getById(props.taskId)
      subtasks = task
        ? ArrUtil.filterUndefined(
            SubtaskUtil.getSubtaskIds(task)
              .map(x => TaskUtil.getById(x))
              .filter(t => t && !isDeletedEntity(t))
          )
        : []

      if (subtasks.length === 0) {
        return null
      }
    }

    return (
      <>
        <div className="flex justify-between my-8 ml-32 text-12">
          <div>{Lang.msg.subtask.subtasksTree}</div>
          <Link
            className="mr-12"
            onClick={e => {
              e.stopPropagation()
              props.onClose()
            }}
          >
            {Lang.msg.subtask.rollup}
          </Link>
        </div>

        <StoreContext.Provider value={store}>
          {parentTaskIds.map((taskId, i, arr) => (
            <ParentTask
              key={i}
              taskId={taskId}
              last={i === arr.length - 1}
              subtasks={subtasks}
            />
          ))}
        </StoreContext.Provider>
      </>
    )
  }
)

const TwColumnParentTasks = tw.div`
    react-root
    bg-chat-list-hover-old
    pb-4
    px-4
    border-t
    with-border
    border-0
`

export const ColumnParentTasks = (props: {
  task: Hub
  onClose: () => void
  className?: string
  hasSubtask?: boolean
}) => {
  const {task, onClose} = props

  return (
    <TwColumnParentTasks className={props.className}>
      <ParentTasks onBoard taskId={task.id} onClose={onClose} />

      <MirroredTasksWithCaption task={task} hasSubtask={props.hasSubtask} />
    </TwColumnParentTasks>
  )
}

export const MirroredTasksWithCaption = (props: {
  task: Hub
  hasSubtask?: boolean
}) => {
  const {task, hasSubtask} = props
  const mirroredHubs = getAllMirroredHubs(task)

  if (!mirroredHubs?.length) {
    return null
  }

  return (
    <>
      {hasSubtask && (
        <div className="mt-8 w-[calc(100%+8px)] mx-[-4px] border border-solid border-line-base border-b-0"></div>
      )}

      <div className={cn('pl-16 pr-12 mt-8', hasSubtask ? '' : 'mb-8')}>
        <div className="mb-8 ml-20 text-primary text-xs-regular max-h-15">
          {Lang.msg.mirroredTasks.mirrorsTitle}
        </div>

        <MirroredTasks task={task} mirroredHubs={mirroredHubs} />
      </div>
    </>
  )
}
